import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Icon,
  InputNumber,
  Modal,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import moment from 'moment';
import {
  addVoucherLine,
  createVoucher,
  getLocations,
  getSpareMaster,
  getStockTakingVouchers,
  removeVoucherLine,
  subscribeToSparesStore,
  submitVoucher
} from './sparesMockStore';

const { Option } = Select;

const tooltipLabel = (label, tip) => (
  <span className="spares-tooltip-label">
    {label}
    <Tooltip title={tip}>
      <Icon type="info-circle" className="spares-tooltip-icon" />
    </Tooltip>
  </span>
);

const SparesStockTaking = () => {
  const [vouchers, setVouchers] = useState(getStockTakingVouchers());
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm, setCreateForm] = useState({
    date: moment(),
    locationId: undefined,
    subLocationId: undefined
  });
  const [lineDraft, setLineDraft] = useState({ spareId: undefined, quantityFound: null });
  const [spareSelectOpen, setSpareSelectOpen] = useState(false);
  const spareSelectRef = useRef(null);
  const spareSelectWrapRef = useRef(null);
  const quantityInputRef = useRef(null);

  const focusSpareSearchInput = () => {
    const selectInstance = spareSelectRef.current;
    if (selectInstance && selectInstance.focus) {
      selectInstance.focus();
    }

    setTimeout(() => {
      const wrap = spareSelectWrapRef.current;
      const searchInput = wrap
        ? wrap.querySelector('.ant-select-search__field')
        : document.querySelector('.ant-select-search__field');
      const selector = wrap
        ? wrap.querySelector('.ant-select-selection')
        : null;

      if (selector) {
        selector.click();
      }
      if (searchInput) {
        searchInput.focus();
        searchInput.select();
      }
    }, 80);
  };

  const focusQuantityInput = () => {
    setTimeout(() => {
      const quantityInstance = quantityInputRef.current;
      if (quantityInstance && quantityInstance.focus) {
        quantityInstance.focus();
      }

      const input = document.querySelector('.spares-quantity-input input');
      if (input) {
        input.focus();
        input.select();
      }
    }, 50);
  };

  const locations = useMemo(() => getLocations(), []);
  const spareMaster = useMemo(() => getSpareMaster(), []);

  useEffect(() => {
    const unsubscribe = subscribeToSparesStore((nextVouchers) => {
      setVouchers(nextVouchers);
    });
    return unsubscribe;
  }, []);

  const selectedVoucher = useMemo(
    () => vouchers.find((voucher) => voucher.id === selectedVoucherId) || null,
    [selectedVoucherId, vouchers]
  );

  const selectedLocation = locations.find((location) => location.id === selectedVoucher?.locationId);
  const availableSubLocations = selectedLocation ? selectedLocation.subLocations : [];
  const selectedSpare = spareMaster.find((spare) => spare.id === lineDraft.spareId);

  const voucherStats = useMemo(() => {
    const draftCount = vouchers.filter((voucher) => voucher.status === 'Draft').length;
    const submittedCount = vouchers.filter((voucher) => voucher.status === 'Submitted').length;
    const totalLines = vouchers.reduce((sum, voucher) => sum + voucher.lines.length, 0);
    return { draftCount, submittedCount, totalLines };
  }, [vouchers]);

  const getLocationLabel = (voucher) => {
    const location = locations.find((item) => item.id === voucher.locationId);
    const subLocation = location?.subLocations.find((item) => item.id === voucher.subLocationId);
    return subLocation ? `${location.name} / ${subLocation.name}` : location?.name || '-';
  };

  const voucherRows = vouchers.map((voucher) => ({
    ...voucher,
    key: voucher.id,
    locationLabel: getLocationLabel(voucher)
  }));

  const stockColumns = [
    {
      title: tooltipLabel('Voucher', 'Unique stock-taking voucher number. Open it to continue counting or review the submitted report.'),
      dataIndex: 'voucherNumber',
      render: (value, record) => (
        <button type="button" className="spares-link-button" onClick={() => setSelectedVoucherId(record.id)}>
          {value}
        </button>
      )
    },
    {
      title: tooltipLabel('Date', 'The physical stock-taking date recorded for this voucher.'),
      dataIndex: 'date',
      render: (value) => moment(value).format('DD MMM YYYY')
    },
    {
      title: tooltipLabel('Location', 'Location and optional sub-location covered by the stock-taking exercise.'),
      dataIndex: 'locationLabel'
    },
    {
      title: tooltipLabel('Status', 'Draft means counting can continue. Submitted means the voucher is locked and ready for reconciliation.'),
      dataIndex: 'status',
      render: (status) => (
        <Tag color={status === 'Submitted' ? 'green' : 'gold'}>{status}</Tag>
      )
    },
    {
      title: tooltipLabel('Lines', 'Number of spare lines already captured in this voucher.'),
      dataIndex: 'lines',
      align: 'right',
      render: (lines) => lines.length
    },
    {
      title: tooltipLabel('Action', 'Open a draft voucher to continue counting or jump to reconciliation for a submitted voucher.'),
      render: (text, record) =>
        record.status === 'Submitted' ? (
          <Button
            size="small"
            type="primary"
            ghost
            onClick={() => {
              window.location.hash = `#/spares-reconciliation?voucherId=${record.id}`;
            }}
          >
            Reconcile
          </Button>
        ) : (
          <Button size="small" onClick={() => setSelectedVoucherId(record.id)}>
            Open
          </Button>
        )
    }
  ];

  const lineColumns = [
    {
      title: tooltipLabel('Spare Code', 'Unique code from the spare master used to identify the item.'),
      dataIndex: 'spareId',
      render: (spareId) => spareMaster.find((spare) => spare.id === spareId)?.code || spareId
    },
    {
      title: tooltipLabel('Spare Name', 'Description of the spare selected from the spare master.'),
      dataIndex: 'spareId',
      render: (spareId) => spareMaster.find((spare) => spare.id === spareId)?.name || '-'
    },
    {
      title: tooltipLabel('Category', 'Master category used to group similar spare items.'),
      dataIndex: 'spareId',
      render: (spareId) => spareMaster.find((spare) => spare.id === spareId)?.category || '-'
    },
    {
      title: tooltipLabel('UOM', 'Unit of measure pulled automatically from the spare master.'),
      dataIndex: 'spareId',
      width: 100,
      render: (spareId) => spareMaster.find((spare) => spare.id === spareId)?.uom || '-'
    },
    {
      title: tooltipLabel('Quantity Found', 'Physical quantity counted during stock taking.'),
      dataIndex: 'quantityFound',
      align: 'right',
      render: (value) => Number(value).toLocaleString()
    },
    {
      title: tooltipLabel('Action', 'Remove a line while the voucher is still in draft. Submitted vouchers are locked.'),
      width: 110,
      render: (text, record) =>
        selectedVoucher?.status === 'Draft' ? (
          <Button size="small" type="danger" ghost onClick={() => removeVoucherLine(selectedVoucher.id, record.id)}>
            Remove
          </Button>
        ) : (
          <Tag color="blue">Locked</Tag>
        )
    }
  ];

  const handleCreateVoucher = () => {
    if (!createForm.date || !createForm.locationId) {
      message.error('Date and location are required to create a stock taking voucher.');
      return;
    }

    const newVoucher = createVoucher({
      date: createForm.date,
      locationId: createForm.locationId,
      subLocationId: createForm.subLocationId
    });

    setSelectedVoucherId(newVoucher.id);
    setCreateModalVisible(false);
    setCreateForm({ date: moment(), locationId: undefined, subLocationId: undefined });
    message.success(`Voucher ${newVoucher.voucherNumber} created.`);
  };

  const handleAddLine = () => {
    if (!selectedVoucher || selectedVoucher.status !== 'Draft') return;
    if (!lineDraft.spareId || !lineDraft.quantityFound) {
      message.error('Select a spare and enter the quantity found.');
      return;
    }

    const duplicate = selectedVoucher.lines.some((line) => line.spareId === lineDraft.spareId);
    if (duplicate) {
      message.warning('This spare is already in the voucher. Remove it first if you need to re-enter it.');
      return;
    }

    addVoucherLine(selectedVoucher.id, lineDraft);
    setLineDraft({ spareId: undefined, quantityFound: null });
    setSpareSelectOpen(true);
    message.success('Spare line added to the voucher.');
    focusSpareSearchInput();
  };

  const handleSubmitVoucher = () => {
    if (!selectedVoucher) return;
    if (!selectedVoucher.lines.length) {
      message.error('Add at least one spare line before submitting.');
      return;
    }

    Modal.confirm({
      title: `Submit ${selectedVoucher.voucherNumber}?`,
      content: 'The mock voucher will be locked and become available for reconciliation.',
      okText: 'Submit',
      onOk: () => {
        submitVoucher(selectedVoucher.id);
        message.success('Stock taking report submitted.');
      }
    });
  };

  const createLocation = locations.find((location) => location.id === createForm.locationId);
  const createSubLocations = createLocation ? createLocation.subLocations : [];

  return (
    <div className="spares-page">
      <div className="spares-hero">
          <div>
            <div className="spares-eyebrow">Under Lab Mock Workflow</div>
          <h3>Spares stock taking with guided voucher creation and counted-line capture.</h3>
          <p>
            Create a voucher, record the spares physically found, and hand off the submitted result to reconciliation without leaving the dashboard.
          </p>
        </div>
        <Tooltip title="Start a new mock stock-taking voucher for a selected date and location.">
          <Button type="primary" size="large" icon="plus" onClick={() => setCreateModalVisible(true)}>
            Create Stock Taking Voucher
          </Button>
        </Tooltip>
      </div>

      <Row gutter={16} className="spares-stat-row">
        <Col xs={24} md={8}>
          <Card className="spares-stat-card">
            <Statistic title={tooltipLabel('Draft Vouchers', 'Draft vouchers are still open for editing and line capture.')} value={voucherStats.draftCount} prefix={<Icon type="edit" />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="spares-stat-card">
            <Statistic title={tooltipLabel('Submitted Vouchers', 'Submitted vouchers are locked and can be reconciled against inventory.')} value={voucherStats.submittedCount} prefix={<Icon type="check-circle" />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="spares-stat-card">
            <Statistic title={tooltipLabel('Counted Spare Lines', 'Total number of spare lines captured across all mock stock-taking vouchers.')} value={voucherStats.totalLines} prefix={<Icon type="database" />} />
          </Card>
        </Col>
      </Row>

      {!selectedVoucher ? (
        <Card
          title={tooltipLabel('Voucher Queue', 'List of all mock stock-taking vouchers. Open one to continue counting or review its current status.')}
          extra={<Tooltip title="Total vouchers currently available in the queue."><Tag color="blue">{voucherRows.length} total</Tag></Tooltip>}
          className="spares-panel-card"
        >
          <Table
            rowKey="id"
            columns={stockColumns}
            dataSource={voucherRows}
            pagination={false}
            size="middle"
            locale={{ emptyText: 'No vouchers yet. Create the first stock-taking voucher to begin.' }}
          />
        </Card>
      ) : (
        <Card
          className="spares-panel-card"
          title={selectedVoucher ? tooltipLabel(`Voucher Workspace • ${selectedVoucher.voucherNumber}`, 'Dedicated workspace to edit voucher details, add counted spares, and submit the stock-taking report.') : 'Voucher Workspace'}
          extra={
            <div className="spares-workspace-actions">
              <Tooltip title="Return to the voucher queue without losing the current mock voucher state.">
                <Button onClick={() => setSelectedVoucherId(null)}>
                  <Icon type="arrow-left" />
                  Back to Queue
                </Button>
              </Tooltip>
              {selectedVoucher ? (
                <Tooltip title="Current workflow state of this voucher. Draft can be edited; submitted is locked.">
                  <Tag color={selectedVoucher.status === 'Submitted' ? 'green' : 'gold'}>{selectedVoucher.status}</Tag>
                </Tooltip>
              ) : null}
            </div>
          }
        >
          {selectedVoucher.status === 'Submitted' && (
            <Alert
              type="success"
              showIcon
              className="spares-alert"
              message="This mock report is submitted and ready for reconciliation."
              description={
                <Button
                  type="primary"
                  ghost
                  onClick={() => {
                    window.location.hash = `#/spares-reconciliation?voucherId=${selectedVoucher.id}`;
                  }}
                >
                  Open Reconciliation
                </Button>
              }
            />
          )}

          <div className="spares-form-grid">
            <div className="spares-form-field">
              <span>{tooltipLabel('Date', 'Stock-taking date for this voucher. This remains editable only while the voucher is in draft.')}</span>
              <DatePicker
                value={moment(selectedVoucher.date)}
                disabled
                style={{ width: '100%' }}
              />
            </div>
            <div className="spares-form-field">
              <span>{tooltipLabel('Location', 'Main location where the stock-taking activity is being performed.')}</span>
              <Select
                value={selectedVoucher.locationId}
                disabled
              >
                {locations.map((location) => (
                  <Option key={location.id} value={location.id}>
                    {location.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="spares-form-field">
              <span>{tooltipLabel('Sub Location (Optional)', 'Optional finer storage area, rack, or bin inside the selected location.')}</span>
              <Select
                value={selectedVoucher.subLocationId || undefined}
                allowClear
                disabled
              >
                {availableSubLocations.map((subLocation) => (
                  <Option key={subLocation.id} value={subLocation.id}>
                    {subLocation.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="spares-line-builder">
            <div className="spares-line-builder-head">
              <div>
                <h4>{tooltipLabel('Add Counted Spare', 'Use this section to add each spare physically found during the stock-taking exercise.')}</h4>
                <p>Select from the spare master and capture the quantity physically found.</p>
              </div>
              <Tooltip title="Add the currently selected spare and counted quantity into this voucher.">
                <Button type="primary" onClick={handleAddLine} disabled={selectedVoucher.status !== 'Draft'}>
                  Add Spare
                </Button>
              </Tooltip>
            </div>

            <div className="spares-line-grid">
              <div className="spares-form-field">
                <span>{tooltipLabel('Spare', 'Choose the spare item from the master list. Duplicate spare lines are blocked in this mock.')}</span>
                <div ref={spareSelectWrapRef}>
                <Select
                  ref={spareSelectRef}
                  showSearch
                  open={spareSelectOpen}
                  value={lineDraft.spareId}
                  placeholder="Select spare from master"
                  disabled={selectedVoucher.status !== 'Draft'}
                  optionFilterProp="children"
                  onDropdownVisibleChange={(open) => {
                    setSpareSelectOpen(open);
                    if (open) {
                      focusSpareSearchInput();
                    }
                  }}
                  onChange={(value) => {
                    setLineDraft((current) => ({ ...current, spareId: value }));
                    setSpareSelectOpen(false);
                    focusQuantityInput();
                  }}
                >
                  {spareMaster.map((spare) => (
                    <Option key={spare.id} value={spare.id}>
                      {spare.code} • {spare.name}
                    </Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="spares-form-field">
                <span>{tooltipLabel('Unit of Measure', 'Read-only unit pulled from the selected spare master record.')}</span>
                <div className="spares-readonly-pill">{selectedSpare ? selectedSpare.uom : 'Auto from spare master'}</div>
              </div>
              <div className="spares-form-field">
                <span>{tooltipLabel('Quantity Found', 'Enter the quantity physically counted for the selected spare.')}</span>
                <InputNumber
                  ref={quantityInputRef}
                  className="spares-quantity-input"
                  min={0}
                  value={lineDraft.quantityFound}
                  disabled={selectedVoucher.status !== 'Draft'}
                  onChange={(value) => setLineDraft((current) => ({ ...current, quantityFound: value }))}
                  onPressEnter={handleAddLine}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          <Table
            rowKey="id"
            dataSource={selectedVoucher.lines}
            columns={lineColumns}
            pagination={false}
            locale={{ emptyText: 'No spares added yet. Start with the first counted item.' }}
            className="spares-lines-table"
          />

          <div className="spares-footer-actions">
            <div className="spares-footer-note">
              {selectedVoucher.lines.length} spares captured for {getLocationLabel(selectedVoucher)}
            </div>
            {selectedVoucher.status === 'Draft' ? (
              <Button type="primary" size="large" onClick={handleSubmitVoucher}>
                Submit Report
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                ghost
                onClick={() => {
                  window.location.hash = `#/spares-reconciliation?voucherId=${selectedVoucher.id}`;
                }}
              >
                Continue to Reconciliation
              </Button>
            )}
          </div>
        </Card>
      )}

      <Modal
        title={tooltipLabel('Create Spares Stock Taking Voucher', 'Start a new voucher by choosing the stock-taking date, location, and optional sub-location.')}
        visible={createModalVisible}
        onOk={handleCreateVoucher}
        okText="Create Voucher"
        onCancel={() => setCreateModalVisible(false)}
      >
        <div className="spares-form-grid">
          <div className="spares-form-field">
            <span>{tooltipLabel('Date', 'Date on which the physical stock-taking exercise is being recorded.')}</span>
            <DatePicker
              value={createForm.date}
              onChange={(value) => setCreateForm((current) => ({ ...current, date: value }))}
              style={{ width: '100%' }}
            />
          </div>
          <div className="spares-form-field">
            <span>{tooltipLabel('Location', 'Main location for the new stock-taking voucher.')}</span>
            <Select
              value={createForm.locationId}
              placeholder="Choose location"
              onChange={(value) => setCreateForm((current) => ({ ...current, locationId: value, subLocationId: undefined }))}
            >
              {locations.map((location) => (
                <Option key={location.id} value={location.id}>
                  {location.name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="spares-form-field">
            <span>{tooltipLabel('Sub Location (Optional)', 'Optional rack, bay, or storage area under the selected location.')}</span>
            <Select
              allowClear
              value={createForm.subLocationId}
              placeholder="Choose sub location"
              onChange={(value) => setCreateForm((current) => ({ ...current, subLocationId: value }))}
            >
              {createSubLocations.map((subLocation) => (
                <Option key={subLocation.id} value={subLocation.id}>
                  {subLocation.name}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SparesStockTaking;
